import React, { useEffect, useMemo, useState } from "react";
import { createQuizTemplate, createQuestion, addQuestion } from "../../../components/quiz/quizTemplate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faTrash,
  faCopy,
  faExclamationTriangle,
  faCheckCircle,
  faPlus,
  faEyeSlash,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import * as yup from "yup";

export function QuizBuilder({ initialQuiz, onChange, lockedQuestionIds = [], hasResults = false }) {
  const [quiz, setQuiz] = useState(() => initialQuiz || createQuizTemplate());
  const [collapsedById, setCollapsedById] = useState({});
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);

  // ✅ Classi riutilizzabili (mantenute, ma migliorate)
  const twInput = `
    tw:w-full
    tw:px-4 tw:py-2.5
    tw:rounded-sm
    tw:border tw:border-gray-200
    tw:bg-white
    tw:text-gray-800
    tw:placeholder-gray-400
    tw:shadow-sm
    focus:tw:outline-none
    focus:tw:ring-2 focus:tw:ring-blue-500/20
    focus:tw:border-blue-500
    tw:transition tw:duration-200
  `.trim();

  const twBtnBase = `
    tw:inline-flex tw:items-center tw:gap-2
    tw:px-4 tw:py-2.5
    tw:font-medium tw:rounded-sm
    tw:shadow-sm
    tw:transition tw:duration-200
    hover:tw:scale-[1.02] tw:active:scale-[0.98]
  `.trim();

  useEffect(() => {
    if (typeof onChange === "function") onChange(quiz);
  }, [quiz, onChange]);

  const json = useMemo(() => JSON.stringify(quiz, null, 2), [quiz]);

  const setQuizField = (field, value) => setQuiz((q) => ({ ...q, [field]: value }));

  const addNewQuestion = () => {
    const idBase = quiz.id || "quiz-id";
    const nextIndex = (quiz.questions?.length || 0) + 1;
    const qid = `${idBase}-q${nextIndex}`;
    const newQ = createQuestion({ id: qid, text: `Domanda ${nextIndex}` });
    setQuiz((q) => addQuestion(q, newQ));
  };

  const updateQuestion = (index, updater) => {
    setQuiz((q) => {
      const qs = [...q.questions];
      qs[index] = updater(qs[index]);
      return { ...q, questions: qs };
    });
  };

  const removeQuestion = (index) => {
    setQuiz((q) => {
      const removed = q.questions[index];
      const next = q.questions.filter((_, i) => i !== index);
      if (removed && removed.id) {
        setCollapsedById((prev) => {
          const nextState = { ...prev };
          delete nextState[removed.id];
          return nextState;
        });
      }
      return { ...q, questions: next };
    });
  };

  const addChoice = (qIndex) => {
    updateQuestion(qIndex, (cur) => {
      const nextLetter = String.fromCharCode(97 + (cur.choices?.length || 0));
      const nextId = nextLetter;
      const nextChoices = [...(cur.choices || []), { id: nextId, text: "" }];
      let nextCorrect = Array.isArray(cur.correctChoiceIds) ? [...cur.correctChoiceIds] : [];
      if (nextCorrect.length === 0) nextCorrect = [nextId];
      if (!cur.multiSelect && nextCorrect.length > 1) nextCorrect = [nextCorrect[0]];
      return { ...cur, choices: nextChoices, correctChoiceIds: nextCorrect };
    });
  };

  const removeChoice = (qIndex, cIndex) => {
    updateQuestion(qIndex, (cur) => {
      const removeId = cur.choices[cIndex].id;
      const nextChoices = cur.choices.filter((_, i) => i !== cIndex);
      let nextCorrect = (Array.isArray(cur.correctChoiceIds) ? cur.correctChoiceIds : []).filter(
        (id) => id !== removeId
      );
      if (!cur.multiSelect && nextCorrect.length === 0 && nextChoices.length > 0) {
        nextCorrect = [nextChoices[0].id];
      }
      return { ...cur, choices: nextChoices, correctChoiceIds: nextCorrect };
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const schema = useMemo(() => {
    const choiceSchema = yup.object({
      id: yup
        .string()
        .trim()
        .required("ID opzione obbligatorio"),
      text: yup
        .string()
        .trim()
        .required("Testo opzione obbligatorio"),
    });
    const questionSchema = yup.object({
      id: yup
        .string()
        .trim()
        .required("ID domanda obbligatorio"),
      text: yup
        .string()
        .trim()
        .required("Testo domanda obbligatorio"),
      multiSelect: yup.boolean().notRequired(),
      choices: yup
        .array()
        .of(choiceSchema)
        .min(1, "Aggiungi almeno una opzione"),
      correctChoiceIds: yup
        .array()
        .of(
          yup
            .string()
            .trim()
            .required()
        )
        .min(1, "Seleziona almeno una risposta corretta")
        .test("ids-esistono", "Risposte corrette non corrispondono alle opzioni", function(value) {
          const ids = (this.parent.choices || []).map((c) => c.id);
          return (value || []).every((id) => ids.includes(id));
        })
        .test("singola-quando-non-multi", "Solo una risposta corretta consentita", function(value) {
          const m = !!this.parent.multiSelect;
          return m || !Array.isArray(value) || value.length <= 1;
        }),
    });
    return yup.object({
      id: yup
        .string()
        .trim()
        .required("Quiz ID obbligatorio"),
      videoId: yup
        .string()
        .trim()
        .required("Video ID obbligatorio"),
      title: yup
        .string()
        .trim()
        .required("Titolo obbligatorio"),
      questions: yup
        .array()
        .of(questionSchema)
        .min(1, "Aggiungi almeno una domanda"),
    });
  }, []);

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(quiz, { abortEarly: false });
        setErrors({});
      } catch (err) {
        const next = {};
        if (err && err.inner)
          err.inner.forEach((e) => {
            if (e.path && !next[e.path]) next[e.path] = e.message;
          });
        setErrors(next);
      }
    };
    validate();
  }, [quiz, schema]);

  return (
    <div className="tw:max-w-12xl tw:mx-auto tw:p-4 tw:space-y-8">
      {/* === Impostazioni Quiz === */}
      <section className="tw:bg-white tw:rounded-xl tw:overflow-hidden tw:border tw:border-gray-100">
        {/* <div className="tw:bg-gradient-to-r tw:from-blue-50 tw:to-indigo-50 tw:px-6 tw:py-4">
          <h2 className="tw:text-xl tw:font-bold tw:text-gray-800 tw:flex tw:items-center tw:gap-2">
            <span className="tw:bg-blue-500 tw:text-white tw:w-8 tw:h-8 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-sm">
              1
            </span>
            Impostazioni Quiz
          </h2>
        </div> */}
        <div className="tw:p-6 tw:space-y-4">
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-1 tw:gap-4">
            {/* <div>
              <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">Video ID</label>
              <input
                className={`${twInput} ${errors["videoId"] ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""}`}
                value={quiz.videoId || ""}
                onChange={(e) => setQuizField("videoId", e.target.value)}
                placeholder="es. v-12345"
              />
              {errors["videoId"] && (
                <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                  {errors["videoId"]}
                </div>
              )}
            </div> */}
            <div>
              <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">Titolo</label>
              <input
                className={`${twInput} ${errors["title"] ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""}`}
                value={quiz.title || ""}
                onChange={(e) => setQuizField("title", e.target.value)}
                placeholder="es. Verifica conoscenze"
              />
              {errors["title"] && (
                <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                  {errors["title"]}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* === Domande === */}
      <section className="tw:bg-white tw:rounded-xl tw:overflow-hidden tw:border tw:border-gray-100">
        <div className="tw:bg-gradient-to-r tw:from-white-50 tw:to-white-50 tw:px-6 tw:py-4">
          <div className="tw:flex tw:items-center tw:justify-between">
            <h2 className="tw:text-xl tw:text-gray-800 tw:flex tw:items-center tw:gap-2">
              {/* <span className="tw:bg-amber-500 tw:text-white tw:w-8 tw:h-8 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-sm">
                2
              </span> */}
              {quiz.questions?.length || 0} Domande
            </h2>
            <button
              className={`${twBtnBase} tw:outline-2 tw:outline-blue-600 tw:text-blue-700`}
              onClick={addNewQuestion}
            >
              <FontAwesomeIcon icon={faPlus} />
              Aggiungi domanda
            </button>
          </div>
        </div>

        <div className="tw:p-6 tw:space-y-6">
          {quiz.questions?.length === 0 ? (
            <div className="tw:text-center tw:py-12 tw:text-gray-500">
              <div className="tw:text-4xl">❓</div>
              <p className="tw:mt-2">Nessuna domanda ancora. Clicca “Aggiungi domanda” per iniziare.</p>
            </div>
          ) : (
            quiz.questions?.map((q, qIndex) => {
              const isCollapsed = !!collapsedById[q.id];
              const hasError = errors[`questions[${qIndex}].text`] || errors[`questions[${qIndex}].correctChoiceIds`];
              const key = q.id || `question-${qIndex}-fallback`;
              return (
                <div
                  key={key}
                  className={`tw:rounded-sm tw:overflow-hidden tw:transition-all tw:duration-300 ${
                    hasError
                      ? "tw:border-2 tw:border-red-200 tw:ring-1 tw:ring-red-100"
                      : "tw:border tw:border-gray-200"
                  }`}
                >
                  {/* Header domanda */}
                  <div
                    className={`tw:rounded-t-lg tw:px-5 tw:py-4 tw:flex tw:items-center tw:justify-between tw:cursor-pointer tw:transition-colors ${
                      hasError ? "tw:bg-red-50" : "tw:bg-gray-600 hover:tw:bg-gray-100"
                    }`}
                    onClick={() => setCollapsedById((prev) => ({ ...prev, [q.id]: !prev[q.id] }))}
                  >
                    <div className="tw:flex-1">
                      <div className="tw:flex tw:items-baseline tw:gap-2">
                        <span className={`tw:font-mono tw:text-sm ${hasError ? "tw:text-red-900" : "tw:text-gray-50"}`}>Q{qIndex + 1}</span>
                        <h3 className={`tw:text-base tw:font-semibold ${hasError ? "tw:text-red-900" : "tw:text-gray-50"}`}>{q.text || "Senza testo"}</h3>
                      </div>
                      {hasError && (
                        <div className="tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-red-600">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          Errore:{" "}
                          {errors[`questions[${qIndex}].text`] || errors[`questions[${qIndex}].correctChoiceIds`]}
                        </div>
                      )}
                    </div>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      {q.choices?.length > 0 && (
                        <>
                        <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:bg-gray-200 tw:text-gray-700 tw:rounded-full">
                          {q.choices.length} opzioni
                        </span>
                        <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:bg-gray-200 tw:text-gray-700 tw:rounded-full">
                          {q.multipleChoice ? "Risposta Multipla" : "Risposta Singola"}
                        </span>
                        </>
                      )}
                      <button
                        type="button"
                        className="tw:w-8 tw:h-8 tw:rounded-full tw:bg-white tw:border tw:border-gray-300 tw:flex tw:items-center tw:justify-center tw:text-gray-500 hover:tw:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapsedById((prev) => ({ ...prev, [q.id]: !prev[q.id] }));
                        }}
                        aria-label={isCollapsed ? "Espandi domanda" : "Comprimi domanda"}
                      >
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`tw:transition-transform tw:duration-200 ${isCollapsed ? "" : "tw:rotate-180"}`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Body domanda (animato) */}
                  <div
                    className={`tw:overflow-hidden tw:transition-all tw:duration-300 tw:ease-in-out ${
                      isCollapsed ? "tw:max-h-0" : "tw:max-h-[2000px]"
                    }`}
                  >
                    <div className="tw:px-5 tw:pb-5 tw:pt-2 tw:space-y-5">
                      {/* Testo domanda */}
                      <div>
                        <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                          Testo della domanda
                        </label>
                          <input
                          className={`${twInput} ${
                            errors[`questions[${qIndex}].text`] ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""
                          }`}
                          value={q.text || ""}
                          onChange={({ target: { value } }) =>
                            updateQuestion(qIndex, (cur) => ({ ...cur, text: value }))
                          }
                          placeholder="es. Qual è il risultato di 2 + 2?"
                          disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                        />
                      </div>

                      {/* Risposte multiple */}
                      <div className="tw:flex tw:items-center tw:gap-3">
                        <label className="tw:flex tw:items-center tw:cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!q.multiSelect}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              updateQuestion(qIndex, (cur) => {
                                const multi = checked;
                                let correct = Array.isArray(cur.correctChoiceIds) ? [...cur.correctChoiceIds] : [];
                                if (!multi && correct.length > 1) correct = [correct[0]];
                                else if (!multi && correct.length === 0 && cur.choices?.length > 0) {
                                  correct = [cur.choices[0].id];
                                }
                                return { ...cur, multiSelect: multi, correctChoiceIds: correct };
                              });
                            }}
                            className="tw:w-4 tw:h-4 tw:text-blue-600 tw:rounded tw:cursor-pointer"
                            disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                          />
                          <span className="tw:ml-2 tw:text-sm tw:text-gray-700">Risposte multiple consentite</span>
                        </label>
                      </div>

                      {/* Opzioni */}
                      <div className="tw:space-y-3">
                        <div className="tw:flex tw:items-center tw:justify-between">
                          <h4 className="tw:text-base tw:font-medium tw:text-gray-800 tw:flex tw:items-center tw:gap-2">
                            <span className="tw:w-6 tw:h-6 tw:bg-green-100 tw:text-green-800 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-xs">
                              A
                            </span>
                            Opzioni
                          </h4>
                          <button
                            className={`${twBtnBase} tw:outline-2 tw:outline-emerald-600 tw:text-emerald-600 hover:tw:bg-emerald-700 tw:shadow`}
                            onClick={() => addChoice(qIndex)}
                            disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            Aggiungi opzione
                          </button>
                        </div>

                        <div className="tw:space-y-3">
                          {q.choices?.map((c, cIndex) => {
                            // ✅ Difesa: se id manca, genera uno temporaneo (non ideale, ma evita crash)
                            const key = c.id || `choice-${q.id}-fallback-${cIndex}`;
                            const isCorrect = (q.correctChoiceIds || []).includes(c.id);
                            return (
                              <div
                                key={key}
                                className={`tw:p-4 tw:rounded-sm tw:border-2 tw:transition-all ${
                                  isCorrect
                                    ? "tw:border-green-400 tw:bg-green-50"
                                    : "tw:border-gray-200 hover:tw:border-gray-300 tw:bg-white"
                                }`}
                              >
                                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-4 tw:gap-4 tw:items-start">
                                  {/* Testo opzione */}
                                  <div className="tw:md:col-span-2">
                                    <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                                      Testo opzione {c.id.toUpperCase()}
                                    </label>
                                    <div className="tw:relative">
                                      {isCorrect && (
                                        <FontAwesomeIcon
                                          icon={faCheckCircle}
                                          className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-emerald-600"
                                        />
                                      )}
                                      <input
                                        className={`${twInput} tw:pl-10`}
                                        value={c.text || ""}
                                        onChange={({ target: { value } }) =>
                                          updateQuestion(qIndex, (cur) => ({
                                            ...cur,
                                            choices: cur.choices.map((ch, i) =>
                                              i === cIndex ? { ...ch, text: value } : ch
                                            ),
                                          }))
                                        }
                                        placeholder="es. 4"
                                        disabled={lockedQuestionIds.includes(q.id)}
                                      />
                                    </div>
                                    {errors[`questions[${qIndex}].choices[${cIndex}].text`] && (
                                      <div className="tw:mt-1 tw:text-xs tw:text-red-600">
                                        {errors[`questions[${qIndex}].choices[${cIndex}].text`]}
                                      </div>
                                    )}
                                  </div>

                                  <div className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center">
                                    <div className="tw:md:col-span-2">
                                      <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                                        Risposta Corretta
                                      </label>
                                      <label className="tw:flex tw:items-center tw:gap-3 tw:cursor-pointer tw:select-none tw:px-3 tw:py-2 tw:rounded-md hover:tw:bg-green-50">
                                        <input
                                          type="checkbox"
                                          checked={isCorrect}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            updateQuestion(qIndex, (cur) => {
                                              let correct = new Set(cur.correctChoiceIds || []);
                                              if (checked) correct.add(c.id);
                                              else correct.delete(c.id);
                                              return { ...cur, correctChoiceIds: Array.from(correct) };
                                            });
                                          }}
                                        className="tw:w-8 tw:h-8 tw:accent-emerald-600 tw:rounded tw:cursor-pointer focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-emerald-500/30"
                                          disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                {/* Cestino */}
                                <div className="tw:mt-3 tw:flex tw:justify-end">
                                  <button
                                    className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-red-600 hover:tw:text-red-800"
                                    onClick={() => removeChoice(qIndex, cIndex)}
                                    aria-label="Rimuovi opzione"
                                    disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                    Rimuovi
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {errors[`questions[${qIndex}].correctChoiceIds`] && (
                          <div className="tw:mt-2 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                            {errors[`questions[${qIndex}].correctChoiceIds`]}
                          </div>
                        )}

                        {/* Pulsante rimuovi domanda */}
                        <div className="tw:pt-4 tw:border-t tw:border-gray-200 tw:flex tw:justify-end">
                          <button
                            className={`${twBtnBase} tw:outline-2 tw:outline-rose-600 tw:text-rose-600 hover:tw:bg-rose-700 tw:shadow`}
                             onClick={() => removeQuestion(qIndex)}
                            disabled={hasResults ? (q.id ? lockedQuestionIds.includes(q.id) : true) : false}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Rimuovi domanda
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* === Esporta JSON === */}
      <section className="tw:hidden tw:bg-white tw:rounded-xl tw:shadow-md tw:overflow-hidden tw:border tw:border-gray-100">
        <div className="tw:bg-gradient-to-r tw:from-gray-50 tw:to-gray-100 tw:px-6 tw:py-4">
          <h2 className="tw:text-xl tw:font-bold tw:text-gray-800 tw:flex tw:items-center tw:gap-2">
            <span className="tw:bg-gray-600 tw:text-white tw:w-8 tw:h-8 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-sm">
              3
            </span>
            Esporta Configurazione
          </h2>
        </div>
        <div className="tw:p-6 tw:space-y-4">
          <div className="tw:bg-gray-200 tw:rounded-sm tw:p-4 tw:font-mono tw:text-sm tw:text-green-400 tw:overflow-x-auto">
            <pre className="tw:m-0">{json}</pre>
          </div>
          <div className="tw:flex tw:gap-3">
            <button
              className={`${twBtnBase} tw:bg-blue-600 tw:text-white hover:tw:bg-blue-700 tw:shadow tw:w-auto`}
              onClick={copyToClipboard}
            >
              <FontAwesomeIcon icon={faCopy} />
              {copied ? "Copiato!" : "Copia JSON"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default QuizBuilder;
